/**
 * Treatment plan under the dental chart (Figma node 7147:4509).
 *
 * Rows come from suggestPlan(); the dentist can change sittings, accept a
 * row, remove a suggestion (kept as "cancelled" so it does not come back),
 * add any catalogue procedure, and save a tooth's plan as a template or
 * favourite. Any edit marks the row "manual", which the engine never touches.
 * No prices: billing picks up completed items later.
 */
import React, { useMemo, useState } from "react";
import {
  CONDITION_BY_KEY,
  PERMANENT_TEETH,
  PRIMARY_TEETH,
  PROCEDURES,
  type ConditionKey,
  type Procedure,
  type ProcedureCode,
} from "../../constants/dental.constants";
import type { DentalFinding, PlanItem } from "../../schemas/dental.schema";
import type { DoctorTemplate, DoctorTemplateCreate } from "../../services/doctor-template.service";
import { isPrimaryTooth, quadrantOf, sortPlan, type TemplateItem } from "../../utils/dental-plan";
import { MAX_PLAN_ITEMS } from "../../utils/dental-chart";

const VALID_TEETH = new Set([...PERMANENT_TEETH, ...PRIMARY_TEETH]);

interface Props {
  items: PlanItem[];
  onChange: (items: PlanItem[]) => void;
  findings: DentalFinding[];
  favouriteNames: Partial<Record<ConditionKey, string>>;
  onSaveTemplate: (body: DoctorTemplateCreate) => Promise<unknown>;
  /** The signed-in doctor's templates; applied only when canUseTemplates. */
  templates: DoctorTemplate[];
  /**
   * Templates belong to the signed-in doctor. They are offered only when that
   * doctor is the visit's doctor, so an admin charting for a colleague never
   * saves into, or applies from, their own templates.
   */
  canUseTemplates: boolean;
}

const where = (i: PlanItem) => (i.tooth !== null ? String(i.tooth) : i.quadrant !== null ? `Q${i.quadrant}` : "Mouth");

export const TreatmentPlanPanel: React.FC<Props> = ({ items, onChange, findings, favouriteNames, onSaveTemplate, templates, canUseTemplates }) => {
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const active = items.filter((i) => i.status !== "cancelled");
  const dismissed = items.length - active.length;

  const edit = (id: string, patch: Partial<PlanItem>) =>
    onChange(items.map((i) => (i.id === id ? { ...i, ...patch, source: "manual" } : i)));
  const dismiss = (id: string) => onChange(items.map((i) => (i.id === id ? { ...i, status: "cancelled" } : i)));
  const restoreDismissed = () => onChange(items.map((i) => (i.status === "cancelled" ? { ...i, status: "proposed" } : i)));

  const favouriteNotes = useMemo(() => {
    const notes: string[] = [];
    for (const f of findings) {
      for (const c of f.conditions) {
        const name = favouriteNames[c];
        if (name) notes.push(`Tooth ${f.tooth} uses your favourite "${name}" for ${CONDITION_BY_KEY[c].label.toLowerCase()}.`);
      }
    }
    return notes;
  }, [findings, favouriteNames]);

  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[15px] font-bold text-slate-800">Treatment Plan</h2>
        <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700">From chart</span>
        {canUseTemplates && (
          <button
            type="button"
            onClick={() => setApplying(true)}
            disabled={templates.length === 0 || findings.length === 0}
            title={templates.length === 0 ? "You have no saved templates yet." : findings.length === 0 ? "Chart a tooth first." : undefined}
            className="ml-auto rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            Apply template
          </button>
        )}
        {canUseTemplates && <button
          type="button"
          onClick={() => setSaving(true)}
          disabled={!findings.some((f) => f.conditions.length > 0) || active.every((i) => i.tooth === null)}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        >
          Save as template
        </button>}
      </div>

      {!canUseTemplates && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Templates and favourites belong to the visit's doctor. Sign in as that doctor to apply or save them.
        </p>
      )}

      {favouriteNotes.map((n) => (
        <p key={n} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{n}</p>
      ))}

      {active.length === 0 ? (
        <p className="text-xs text-slate-500">Nothing to treat yet. Chart a condition, or add a procedure.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[480px] text-left tabular-nums">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Tooth</th>
                <th className="px-3 py-2">Procedure</th>
                <th className="px-3 py-2">Sittings</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2"><span className="sr-only">Source</span></th>
                <th className="px-3 py-2"><span className="sr-only">Remove</span></th>
              </tr>
            </thead>
            <tbody>
              {active.map((i) => (
                <tr key={i.id} className="border-t border-slate-100">
                  <td className={`px-3 py-2 text-[13px] font-semibold ${i.tooth !== null ? "text-blue-700" : "text-slate-500"}`}>{where(i)}</td>
                  <td className="px-3 py-2 text-[13px] text-slate-800">{i.label}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5 text-[13px]">
                      <button type="button" aria-label="Fewer sittings" disabled={i.sittings_planned <= 1}
                        onClick={() => edit(i.id, { sittings_planned: i.sittings_planned - 1 })}
                        className="h-5 w-5 rounded bg-slate-100 text-slate-500 disabled:opacity-40">−</button>
                      {i.sittings_planned}
                      <button type="button" aria-label="More sittings" disabled={i.sittings_planned >= 20}
                        onClick={() => edit(i.id, { sittings_planned: i.sittings_planned + 1 })}
                        className="h-5 w-5 rounded bg-slate-100 text-slate-500 disabled:opacity-40">+</button>
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button type="button"
                      onClick={() => edit(i.id, { status: i.status === "accepted" ? "proposed" : "accepted" })}
                      className={`rounded-full border px-2.5 py-0.5 text-[11.5px] ${i.status === "accepted" ? "border-transparent bg-green-100 text-green-700" : "border-slate-200 text-slate-600"}`}>
                      {i.status === "accepted" ? "Accepted" : i.status === "proposed" ? "Proposed" : i.status.replace("_", " ")}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] ${i.source === "manual" ? "bg-orange-50 text-orange-700" : "bg-sky-50 text-sky-700"}`}>
                      {i.source === "manual" ? "Yours" : "Auto"}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <button type="button" aria-label={`Remove ${i.label}`} onClick={() => dismiss(i.id)}
                      className="rounded px-1.5 text-base leading-none text-slate-400 hover:bg-slate-100">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dismissed > 0 && (
        <p className="text-xs text-slate-500">
          {dismissed} suggestion{dismissed === 1 ? "" : "s"} removed.{" "}
          <button type="button" onClick={restoreDismissed} className="font-semibold text-sky-700">Bring back</button>
        </p>
      )}

      {adding ? (
        <AddProcedure
          findings={findings}
          onCancel={() => setAdding(false)}
          onAdd={(item) => { onChange(sortPlan([...items, item])); setAdding(false); }}
        />
      ) : (
        <button type="button" onClick={() => setAdding(true)} disabled={items.length >= MAX_PLAN_ITEMS}
          title={items.length >= MAX_PLAN_ITEMS ? `A plan holds at most ${MAX_PLAN_ITEMS} procedures.` : undefined}
          className="rounded-lg border border-slate-200 py-2 text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-40">
          + Add procedure
        </button>
      )}

      <p className="text-[11px] text-slate-500">Prints on the prescription. No prices on the plan.</p>

      {applying && (
        <ApplyTemplateDialog
          templates={templates}
          findings={findings}
          onClose={() => setApplying(false)}
          onApply={(added) => { onChange(sortPlan(mergeApplied(items, added))); setApplying(false); }}
        />
      )}

      {saving && (
        <SaveTemplateDialog
          items={active}
          findings={findings}
          onClose={() => setSaving(false)}
          onSave={onSaveTemplate}
        />
      )}
    </div>
  );
};

// ─── Add procedure ────────────────────────────────────────────────────────────

const AddProcedure: React.FC<{
  findings: DentalFinding[];
  onAdd: (item: PlanItem) => void;
  onCancel: () => void;
}> = ({ findings, onAdd, onCancel }) => {
  const [code, setCode] = useState<ProcedureCode>("composite_restoration");
  const [tooth, setTooth] = useState(findings[0] ? String(findings[0].tooth) : "");
  const toothNo = Number(tooth);
  // Milk-tooth procedures (pulpotomy, stainless steel crown, space maintainer)
  // are hidden for an adult tooth, and adult-only ones for a milk tooth.
  const choices = proceduresForTooth(VALID_TEETH.has(toothNo) ? toothNo : null);
  const proc = choices.find((p) => p.code === code) ?? choices[0];
  const needsTooth = proc.scope !== "mouth";
  const toothOk = !needsTooth || VALID_TEETH.has(toothNo);

  const add = () => {
    if (!toothOk) return;
    onAdd({
      id: crypto.randomUUID(),
      tooth: proc.scope === "tooth" ? toothNo : null,
      quadrant: proc.scope === "quadrant" ? quadrantOf(toothNo) : null,
      procedure_code: proc.code,
      label: proc.label,
      sittings_planned: proc.sittings,
      status: "proposed",
      source: "manual",
    });
  };

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 p-3">
      <label className="flex min-w-[200px] flex-1 flex-col gap-1 text-xs text-slate-600" htmlFor="dental-add-procedure">
        Procedure
        <select id="dental-add-procedure" value={proc.code} onChange={(e) => setCode(e.target.value as ProcedureCode)}
          className="rounded-lg bg-slate-100 px-2 py-2 text-sm text-slate-800">
          {choices.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
        </select>
      </label>
      {needsTooth && (
        <label className="flex w-28 flex-col gap-1 text-xs text-slate-600" htmlFor="dental-add-tooth">
          {proc.scope === "quadrant" ? "Any tooth in quadrant" : "Tooth"}
          <input id="dental-add-tooth" inputMode="numeric" value={tooth} onChange={(e) => setTooth(e.target.value.replace(/\D/g, "").slice(0, 2))}
            list="dental-charted-teeth" aria-invalid={!toothOk}
            className={`rounded-lg bg-slate-100 px-2 py-2 text-sm ${toothOk ? "text-slate-800" : "text-red-600 ring-1 ring-red-300"}`} />
          <datalist id="dental-charted-teeth">{findings.map((f) => <option key={f.tooth} value={f.tooth} />)}</datalist>
        </label>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">Cancel</button>
        <button type="button" onClick={add} disabled={!toothOk} className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">Add</button>
      </div>
      {!toothOk && <p className="w-full text-xs text-red-600">Enter an FDI tooth number, such as 36 or 75.</p>}
    </div>
  );
};

// ─── Procedures that fit a tooth ──────────────────────────────────────────────

export function proceduresForTooth(tooth: number | null): readonly Procedure[] {
  if (tooth === null) return PROCEDURES;
  const dentition = isPrimaryTooth(tooth) ? "primary" : "permanent";
  return PROCEDURES.filter((p: Procedure) => !p.dentition || p.dentition === dentition);
}

// ─── Apply template ───────────────────────────────────────────────────────────

const itemKey = (i: Pick<PlanItem, "procedure_code" | "tooth" | "quadrant">) => `${i.procedure_code}|${i.tooth ?? ""}|${i.quadrant ?? ""}`;

/** Add applied items, skipping any procedure already planned at the same place. */
export function mergeApplied(items: PlanItem[], added: PlanItem[]): PlanItem[] {
  const planned = new Set(items.filter((i) => i.status !== "cancelled").map(itemKey));
  return [...items, ...added.filter((i) => !planned.has(itemKey(i)))];
}

/** A template's procedures placed on one tooth, as the dentist's own rows. */
export function itemsFromTemplate(template: DoctorTemplate, tooth: number): PlanItem[] {
  return template.payload.items.map((t) => ({
    id: crypto.randomUUID(),
    tooth: t.scope === "tooth" ? tooth : null,
    quadrant: t.scope === "quadrant" ? quadrantOf(tooth) : null,
    procedure_code: t.procedure_code,
    label: t.label,
    sittings_planned: t.sittings_planned,
    status: "proposed",
    source: "manual",
  }));
}

const ApplyTemplateDialog: React.FC<{
  templates: DoctorTemplate[];
  findings: DentalFinding[];
  onApply: (items: PlanItem[]) => void;
  onClose: () => void;
}> = ({ templates, findings, onApply, onClose }) => {
  const [templateId, setTemplateId] = useState(templates[0]?.template_id ?? "");
  const template = templates.find((t) => t.template_id === templateId);
  // Teeth charted with the template's condition first, then every charted tooth.
  const teeth = useMemo(() => {
    const all = findings.map((f) => f.tooth);
    const match = template?.condition_key ? findings.filter((f) => f.conditions.includes(template.condition_key as ConditionKey)).map((f) => f.tooth) : [];
    return [...match, ...all.filter((t) => !match.includes(t))];
  }, [findings, template]);
  const [tooth, setTooth] = useState<number | null>(teeth[0] ?? null);
  const chosenTooth = tooth !== null && teeth.includes(tooth) ? tooth : teeth[0] ?? null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="dental-apply-title"
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div>
          <h3 id="dental-apply-title" className="text-lg font-bold text-slate-900">Apply template</h3>
          <p className="text-sm text-slate-500">Adds the template's procedures for one tooth. You can edit them after.</p>
        </div>
        <label htmlFor="dental-apply-template" className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
          Template
          <select id="dental-apply-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-normal text-slate-800">
            {templates.map((t) => (
              <option key={t.template_id} value={t.template_id}>
                {t.name}{t.condition_key ? ` (${CONDITION_BY_KEY[t.condition_key as ConditionKey]?.label ?? t.condition_key})` : ""}{t.is_favourite ? " ★" : ""}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor="dental-apply-tooth" className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600">
          Tooth
          <select id="dental-apply-tooth" value={chosenTooth ?? ""} onChange={(e) => setTooth(Number(e.target.value))}
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-normal text-slate-800">
            {teeth.map((t) => <option key={t} value={t}>Tooth {t}</option>)}
          </select>
        </label>
        {template && (
          <ul className="rounded-xl border border-slate-200 text-sm">
            {template.payload.items.map((i) => (
              <li key={i.procedure_code} className="flex justify-between border-t border-slate-100 px-3 py-2 first:border-t-0">
                <span>{i.label}</span>
                <span className="text-xs text-slate-500">{i.sittings_planned} sitting{i.sittings_planned === 1 ? "" : "s"}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">Cancel</button>
          <button type="button" disabled={!template || chosenTooth === null}
            onClick={() => template && chosenTooth !== null && onApply(itemsFromTemplate(template, chosenTooth))}
            className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Save as template ─────────────────────────────────────────────────────────

const SaveTemplateDialog: React.FC<{
  items: PlanItem[];
  findings: DentalFinding[];
  onSave: (body: DoctorTemplateCreate) => Promise<unknown>;
  onClose: () => void;
}> = ({ items, findings, onSave, onClose }) => {
  // One choice per (tooth, condition): the template saves that tooth's procedures.
  const options = findings.flatMap((f) => f.conditions.map((c) => ({ key: `${f.tooth}:${c}`, tooth: f.tooth, condition: c })));
  const [choice, setChoice] = useState(options[0]?.key ?? "");
  const picked = options.find((o) => o.key === choice);
  const [name, setName] = useState("");
  const [favourite, setFavourite] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Procedures left out of the template. A tooth can carry procedures for
  // several conditions; the dentist keeps only the ones for this condition.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const toggle = (code: string) => setExcluded((s) => {
    const next = new Set(s);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });

  const toothItems: TemplateItem[] = picked
    ? items.filter((i) => i.tooth === picked.tooth).map((i) => ({
        procedure_code: i.procedure_code,
        label: i.label,
        sittings_planned: i.sittings_planned,
        scope: "tooth",
      }))
    : [];
  const templateItems = toothItems.filter((t) => !excluded.has(t.procedure_code));

  const save = async () => {
    if (!picked || templateItems.length === 0 || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSave({ kind: "dental_plan", condition_key: picked.condition, name: name.trim(), payload: { items: templateItems }, is_favourite: favourite });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the template. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="dental-save-title"
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div>
          <h3 id="dental-save-title" className="text-lg font-bold text-slate-900">Save treatment plan</h3>
          <p className="text-sm text-slate-500">Saved to your templates for this condition. Only you can see it.</p>
        </div>
        <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600" htmlFor="dental-save-condition">
          Condition
          <select id="dental-save-condition" value={choice} onChange={(e) => { setChoice(e.target.value); setExcluded(new Set()); }}
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-normal text-slate-800">
            {options.map((o) => (
              <option key={o.key} value={o.key}>{CONDITION_BY_KEY[o.condition].label} (tooth {o.tooth})</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600" htmlFor="dental-save-name">
          Name
          <input id="dental-save-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)}
            placeholder="My RCT plan" className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-normal text-slate-800" />
        </label>
        <div className="rounded-xl border border-slate-200">
          <p className="bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Procedures{picked ? ` from tooth ${picked.tooth}` : ""}
          </p>
          {toothItems.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">This tooth has no procedures in the plan.</p>
          ) : toothItems.map((t) => (
            <label key={t.procedure_code} htmlFor={`dental-save-item-${t.procedure_code}`}
              className="flex items-center gap-2 border-t border-slate-100 px-3 py-2 text-sm">
              <input id={`dental-save-item-${t.procedure_code}`} type="checkbox"
                checked={!excluded.has(t.procedure_code)} onChange={() => toggle(t.procedure_code)} />
              <span className="flex-1">{t.label}</span>
              <span className="text-xs text-slate-500">{t.sittings_planned} sitting{t.sittings_planned === 1 ? "" : "s"}</span>
            </label>
          ))}
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-700" htmlFor="dental-save-favourite">
          <input id="dental-save-favourite" type="checkbox" checked={favourite} onChange={(e) => setFavourite(e.target.checked)} className="mt-0.5" />
          Favourite: apply automatically when this condition is charted. Replaces your current favourite for it.
        </label>
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">Cancel</button>
          <button type="button" onClick={save} disabled={busy || !name.trim() || templateItems.length === 0}
            className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            {busy ? "Saving" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanPanel;
